import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import cl from 'classnames';
import Swiper from 'swiper';
import TariffItem from './TariffItem';
import {t} from '../../services/i18nHandler';
import {mediaSizes} from '../../configs/media';
import {USER_ROLE} from '../../constants/user';
import ModalIndividualTariff from './ModalIndividualTariff';

import 'swiper/dist/css/swiper.min.css';

const individualTariff = {
  _id: 'individual',
  name: {ru: 'Индивидуальный', en: 'Individual'},
  subNameText: {ru: 'Вы достойны большего', en: 'You deserve more'},
  shortDescription: {
    ru: 'Тариф расчитывается индивидуально в зависимости от Ваших требований и наших возможностей',
    en: 'The rate is calculated individually depending on your needs and our capabilities.',
  },
};

function TariffsList({
  tariffs, services, detailsClick, language, activePeriod, activeRole, serviceOptions,
}) {
  const swiperRef = useRef();

  const containerRef = useRef();

  const paginationRef = useRef();

  const [activeTariff, setActiveTariff] = useState(null);

  const [showIndividualModal, setShowIndividualModal] = useState(false);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const onWindowResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);

  const responsiveView = useMemo(() => (windowWidth < mediaSizes.xx), [windowWidth]);

  const allTariffs = useMemo(() => ([...tariffs, ...services]), [tariffs, services]);

  const destroySwiper = useCallback(() => {
    if (swiperRef.current) {
      swiperRef.current.destroy(true, true);
    }
  }, []);

  const initSwiper = useCallback(() => {
    destroySwiper();
    swiperRef.current = new Swiper(containerRef.current, {
      slidesPerView: 'auto',
      pagination: {
        el: paginationRef.current,
      },
    });
  }, []);

  const showIndividual = useMemo(() => (activeRole === USER_ROLE.RECRUITER
    || activeRole === USER_ROLE.EMPLOYER), [activeRole]);

  useEffect(() => {
    if (responsiveView) {
      initSwiper();
    } else {
      destroySwiper();
    }
  }, [responsiveView, allTariffs.length]);

  useEffect(() => {
    window.addEventListener('resize', onWindowResize);
    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, []);

  const [showIndividualTariffModal, setShowIndividualTariffModal] = useState(false);

  const [showIndividualServiceModal, setShowIndividualServiceModal] = useState(false);

  const onCloseIndividualTariffModal = useCallback(() => {
    setShowIndividualTariffModal(false);
    setShowIndividualServiceModal(false);
  }, []);

  return (
    <div className={cl('tariffs_list', {
      active: !!activeTariff,
    })}
    >
      {(showIndividualTariffModal || showIndividualServiceModal) && (
        <ModalIndividualTariff
          onClose={onCloseIndividualTariffModal}
          isOpen
          tariffs={showIndividualTariffModal ? tariffs : services}
          serviceOptions={serviceOptions}
          language={language}
        />
      )}
      {!responsiveView && (
        <>
          {!!tariffs.length&& (
            <div className="tariffs_category">
              <div className="tariffs_category_name">
                {t('tariffs.accessRates')}
              </div>
              <div className="tariffs_item-list">
                {tariffs.map((tariff) => (
                  <TariffItem
                    key={tariff._id}
                    tariff={tariff}
                    activeTariff={activeTariff}
                    setActiveTariff={setActiveTariff}
                    detailsClick={() => detailsClick(tariff)}
                    detailsDemoClick={() => detailsClick(tariff, true)}
                    language={language}
                    activePeriod={activePeriod}
                  />
                ))}
                {showIndividual && (
                  <TariffItem
                    isIndividual
                    tariff={individualTariff}
                    activeTariff={activeTariff}
                    setActiveTariff={setActiveTariff}
                    detailsClick={() => setShowIndividualTariffModal(true)}
                    detailsDemoClick={() => {}}
                    language={language}
                    activePeriod={activePeriod}
                  />
                )}
              </div>
            </div>
          )}
          {!!services.length && (
            <div className="tariffs_category">
              <div className="tariffs_category_name">
                {t('tariffs.servicesRates')}
              </div>
              <div className="tariffs_item-list">
                {services.map((tariff) => (
                  <TariffItem
                    key={tariff._id}
                    tariff={tariff}
                    activeTariff={activeTariff}
                    setActiveTariff={setActiveTariff}
                    detailsClick={() => detailsClick(tariff)}
                    detailsDemoClick={() => detailsClick(tariff, true)}
                    language={language}
                    activePeriod={activePeriod}
                  />
                ))}
                {showIndividual && (
                  <TariffItem
                    isIndividual
                    tariff={{...individualTariff, _id: 'individual-service'}}
                    activeTariff={activeTariff}
                    setActiveTariff={setActiveTariff}
                    detailsClick={() => setShowIndividualServiceModal(true)}
                    detailsDemoClick={() => {}}
                    language={language}
                    activePeriod={activePeriod}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}
      {responsiveView && (
        <div className="tariffs_item-list">
          <div className="swiper-pagination" ref={paginationRef} />
          <div className="swiper-container" ref={containerRef}>
            <div className="swiper-wrapper">
              {tariffs.map((tariff) => (
                <div className="swiper-slide" key={tariff._id}>
                  <TariffItem
                    isMobile
                    tariff={tariff}
                    activeTariff={activeTariff}
                    setActiveTariff={setActiveTariff}
                    detailsClick={() => detailsClick(tariff)}
                    detailsDemoClick={() => detailsClick(tariff, true)}
                    language={language}
                    activePeriod={activePeriod}
                  />
                </div>
              ))}
              {showIndividual && !!tariffs.length && (
                <div className="swiper-slide">
                  <TariffItem
                    isIndividual
                    tariff={individualTariff}
                    activeTariff={activeTariff}
                    setActiveTariff={setActiveTariff}
                    detailsClick={() => setShowIndividualTariffModal(true)}
                    detailsDemoClick={() => setShowIndividualModal(true)}
                    language={language}
                    activePeriod={activePeriod}
                  />
                </div>
              )}
              {services.map((tariff) => (
                <div className="swiper-slide" key={tariff._id}>
                  <TariffItem
                    isMobile
                    tariff={tariff}
                    activeTariff={activeTariff}
                    setActiveTariff={setActiveTariff}
                    detailsClick={() => detailsClick(tariff)}
                    detailsDemoClick={() => detailsClick(tariff, true)}
                    language={language}
                    activePeriod={activePeriod}
                  />
                </div>
              ))}
              {showIndividual && !!services.length && (
                <div className="swiper-slide">
                  <TariffItem
                    isIndividual
                    tariff={{...individualTariff, _id: 'individual-service'}}
                    activeTariff={activeTariff}
                    setActiveTariff={setActiveTariff}
                    detailsClick={() => setShowIndividualTariffModal(true)}
                    detailsDemoClick={() => setShowIndividualServiceModal(true)}
                    language={language}
                    activePeriod={activePeriod}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

TariffsList.defaultProps = {
  tariffs: [],
  services: [],
  serviceOptions: [],
  language: 'ru',
};

TariffsList.propTypes = {
  tariffs: PropTypes.arrayOf(PropTypes.shape({})),
  services: PropTypes.arrayOf(PropTypes.shape({})),
  serviceOptions: PropTypes.arrayOf(PropTypes.shape({})),
  detailsClick: PropTypes.func.isRequired,
  language: PropTypes.string,
  activePeriod: PropTypes.number.isRequired,
};

export default memo(TariffsList);
